import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// ScrollArea not needed — parent settings layout handles scrolling
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useRoles,
  useAllPermissions,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useDeleteUser,
  useAssignPermissions,
  useAssignRoleToUsers,
  useCreatePermission,
  useDeletePermission,
} from '@/hooks/use-roles-permissions';
import { useIsSuperAdmin, usePermission } from '@/lib/permissions';
import {
  isSuperAdminOnlyCode,
  canEditRoleColumn,
  mergeEdits,
  moduleCheckState,
  toggleModuleInSet,
} from '@/features/settings/lib/permission-matrix';
import { useAuthStore } from '@/stores/auth';
import { extractErrorMessage } from '@/lib/errors';
import type {
  RoleWithPermissions,
  RoleUser,
  Permission,
  PermissionWithRoleCount,
} from '@hygge/shared';
import { USER_ROLE_LABELS } from '@hygge/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Lock,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { AddEmployeeDialog } from '@/features/people/components/add-employee-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { rolesKeys } from '@/hooks/use-roles-permissions';
import { Link as RouterLink } from '@tanstack/react-router';
import { cn } from '@/lib/utils';

function guessNameFromEmail(email: string): { firstName: string; lastName: string } {
  const local = (email.split('@')[0] ?? '').trim();
  if (!local) return { firstName: '', lastName: '' };
  const parts = local.split(/[._-]+/).filter(Boolean);
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');
  const firstName = cap(parts[0] ?? '');
  const lastName = parts.slice(1).map(cap).join(' ');
  return { firstName, lastName };
}

export const Route = createFileRoute('/settings/roles')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'roles' } });
  },
});

// ─── Main Page ─────────────────────────────────────────────

export function RolesPermissionsPage() {
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Roles & Permissions
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage user roles and configure what each role can access
          </p>
        </div>
      </div>

      <Tabs defaultValue="matrix" className="space-y-6">
        <TabsList
          data-slot="roles-tabs"
          className="bg-transparent rounded-none border-b border-border p-0 h-auto w-full justify-start gap-0"
        >
          {[
            { value: 'matrix', label: 'Permission Matrix', icon: Shield },
            { value: 'roles', label: 'Roles', icon: Lock },
            { value: 'accounts', label: 'Accounts', icon: Users },
            { value: 'permissions', label: 'Permissions', icon: ShieldCheck },
          ].map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="rounded-none border-b-2 bg-transparent px-4 py-2.5 text-sm font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground gap-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground"
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="matrix">
          <PermissionMatrixTab />
        </TabsContent>
        <TabsContent value="roles">
          <RolesTab />
        </TabsContent>
        <TabsContent value="accounts">
          <UserRolesTab />
        </TabsContent>
        <TabsContent value="permissions">
          <PermissionsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Permission Matrix Tab (Hero) ──────────────────────────

type PermissionDraft = Map<string, Set<string>>;

interface RoleDelta {
  role: RoleWithPermissions;
  added: number;
  removed: number;
}

interface MatrixDiff {
  added: number;
  removed: number;
  perRole: RoleDelta[];
}

const EMPTY_DIFF: MatrixDiff = { added: 0, removed: 0, perRole: [] };

function buildDraft(roles: RoleWithPermissions[]): PermissionDraft {
  return new Map(roles.map((r) => [r.id, new Set(r.permissions.map((p) => p.id))]));
}

function diffAgainstServer(roles: RoleWithPermissions[], draft: PermissionDraft): MatrixDiff {
  let added = 0;
  let removed = 0;
  const perRole: RoleDelta[] = [];
  for (const role of roles) {
    const draftIds = draft.get(role.id);
    if (!draftIds) continue;
    const serverIds = new Set(role.permissions.map((p) => p.id));
    let a = 0;
    let r = 0;
    for (const id of draftIds) if (!serverIds.has(id)) a++;
    for (const id of serverIds) if (!draftIds.has(id)) r++;
    if (a || r) {
      added += a;
      removed += r;
      perRole.push({ role, added: a, removed: r });
    }
  }
  return { added, removed, perRole };
}

function groupPermissionsByModule(permissions: PermissionWithRoleCount[] | undefined) {
  if (!permissions) return [];
  const map = new Map<string, PermissionWithRoleCount[]>();
  for (const p of permissions) {
    const list = map.get(p.module) ?? [];
    list.push(p);
    map.set(p.module, list);
  }
  return Array.from(map, ([name, perms]) => ({ name, permissions: perms }));
}

/** Local draft of role→permission assignments with diff against server state. */
function usePermissionMatrixDraft(roles: RoleWithPermissions[] | undefined) {
  // Only this user's edits are state; the draft is derived from them and the
  // latest server sets on every render, so an untouched role follows the
  // server even in the render right after a refetch. [HW-1062]
  const [edits, setEdits] = useState<PermissionDraft>(() => new Map());
  const merged = useMemo(
    () => (roles ? mergeEdits(buildDraft(roles), edits) : null),
    [roles, edits]
  );
  const draft = merged?.draft ?? null;

  // Drop edits the server now holds (after a save) so a later change by
  // someone else to that role is followed, not shadowed.
  useEffect(() => {
    if (!merged || merged.settled.length === 0) return;
    setEdits((prev) => {
      const next = new Map(prev);
      for (const roleId of merged.settled) next.delete(roleId);
      return next;
    });
  }, [merged]);

  const diff = useMemo(
    () => (roles && draft ? diffAgainstServer(roles, draft) : EMPTY_DIFF),
    [roles, draft]
  );

  const editRole = (roleId: string, mutate: (set: Set<string>) => void) => {
    setEdits((prev) => {
      const set = new Set(prev.get(roleId) ?? draft?.get(roleId) ?? []);
      mutate(set);
      return new Map(prev).set(roleId, set);
    });
  };

  const togglePermission = (roleId: string, permId: string) =>
    editRole(roleId, (set) => toggleInSet(set, permId));

  // A non-super_admin's module tick skips the super-admin-only permission and
  // leaves it as it is: the server refuses any save that changes it. [HW-1061]
  const toggleModule = (roleId: string, modulePerms: Permission[], isSuperAdmin: boolean) =>
    editRole(roleId, (set) => toggleModuleInSet(set, modulePerms, isSuperAdmin));

  const discard = () => setEdits(new Map());

  return {
    diff,
    isDirty: diff.perRole.length > 0,
    has: (roleId: string, permId: string) => draft?.get(roleId)?.has(permId) ?? false,
    permissionIds: (roleId: string) => Array.from(draft?.get(roleId) ?? []),
    togglePermission,
    toggleModule,
    discard,
  };
}

function toggleInSet(set: Set<string>, id: string) {
  if (set.has(id)) set.delete(id);
  else set.add(id);
}

function PermissionMatrixTab() {
  const { data: roles, isLoading: rolesLoading } = useRoles();
  const { data: permissions, isLoading: permsLoading } = useAllPermissions();
  const assignMutation = useAssignPermissions();
  const isSuperAdmin = useIsSuperAdmin();
  // Without `settings.roles.manage` the matrix is read-only. [HW-1062]
  const canManageRoles = usePermission('settings.roles.manage');
  const matrix = usePermissionMatrixDraft(roles);

  const [search, setSearch] = useState('');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const modules = useMemo(() => groupPermissionsByModule(permissions), [permissions]);

  const filteredModules = useMemo(() => {
    if (!search.trim()) return modules;
    const q = search.toLowerCase();
    return modules
      .map((m) => ({
        ...m,
        permissions: m.permissions.filter(
          (p) =>
            p.code.toLowerCase().includes(q) ||
            p.name.toLowerCase().includes(q) ||
            p.module.toLowerCase().includes(q)
        ),
      }))
      .filter((m) => m.permissions.length > 0);
  }, [modules, search]);

  const toggleExpand = (name: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const openConfirm = () => {
    setSaveError(null);
    setConfirmOpen(true);
  };

  const handleApply = async () => {
    setSaveError(null);
    try {
      await Promise.all(
        matrix.diff.perRole.map(({ role }) =>
          assignMutation.mutateAsync({
            roleId: role.id,
            permissionIds: matrix.permissionIds(role.id),
          })
        )
      );
      setConfirmOpen(false);
    } catch (err) {
      setSaveError(extractErrorMessage(err, 'Failed to save permissions'));
    }
  };

  if (rolesLoading || permsLoading) return <MatrixLoadingState />;
  if (!roles?.length || !permissions?.length) return <MatrixEmptyState />;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <MatrixToolbar
          search={search}
          onSearchChange={setSearch}
          totalPermissions={permissions.length}
          totalModules={modules.length}
          diff={matrix.diff}
          isDirty={matrix.isDirty}
          isSaving={assignMutation.isPending}
          onDiscard={matrix.discard}
          onSave={openConfirm}
        />

        <div className="overflow-auto max-h-[calc(100vh-25rem)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[280px] sticky left-0 top-0 z-30 bg-background before:content-[''] before:absolute before:inset-0 before:bg-muted/40 before:pointer-events-none">
                  <div className="relative flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5" />
                    Permission
                  </div>
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="text-center p-3 font-medium text-muted-foreground min-w-[120px] sticky top-0 z-20 bg-background before:content-[''] before:absolute before:inset-0 before:bg-muted/40 before:pointer-events-none"
                  >
                    <div className="relative flex flex-col items-center gap-1.5">
                      <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5">
                        {USER_ROLE_LABELS[role.name] || role.name}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground/60">
                        {role._count?.users ?? 0}{' '}
                        {(role._count?.users ?? 0) === 1 ? 'user' : 'users'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredModules.map((mod) => (
                <ModuleGroup
                  key={mod.name}
                  moduleName={mod.name}
                  permissions={mod.permissions}
                  roles={roles}
                  isExpanded={expandedModules.has(mod.name)}
                  isSuperAdmin={isSuperAdmin}
                  canEditRole={(role) =>
                    canEditRoleColumn(role.name, { isSuperAdmin, canManageRoles })
                  }
                  onToggleExpand={() => toggleExpand(mod.name)}
                  onTogglePermission={(role, perm) => matrix.togglePermission(role.id, perm.id)}
                  onToggleModule={(role) => matrix.toggleModule(role.id, mod.permissions, isSuperAdmin)}
                  roleHasPermission={(role, permId) => matrix.has(role.id, permId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <ConfirmSaveDialog
        open={confirmOpen}
        diff={matrix.diff}
        isSaving={assignMutation.isPending}
        error={saveError}
        onCancel={() => {
          setConfirmOpen(false);
          setSaveError(null);
        }}
        onApply={handleApply}
      />
    </Card>
  );
}

function MatrixLoadingState() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

function MatrixEmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Shield className="h-8 w-8 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">No roles or permissions found</p>
      </CardContent>
    </Card>
  );
}

interface MatrixToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  totalPermissions: number;
  totalModules: number;
  diff: MatrixDiff;
  isDirty: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

function MatrixToolbar({
  search,
  onSearchChange,
  totalPermissions,
  totalModules,
  diff,
  isDirty,
  isSaving,
  onDiscard,
  onSave,
}: MatrixToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border">
      <div className="relative flex-1 min-w-[220px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search permissions..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {isDirty ? (
        <DirtyIndicator diff={diff} />
      ) : (
        <p className="text-xs text-muted-foreground hidden sm:block">
          {totalPermissions} permissions across {totalModules} modules
        </p>
      )}

      <TooltipProvider delayDuration={150}>
        <div className="ml-auto flex items-center gap-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onDiscard}
                disabled={!isDirty || isSaving}
                aria-label="Discard changes"
                className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Discard changes</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                onClick={onSave}
                disabled={!isDirty || isSaving}
                aria-label="Save changes"
                className="h-9 w-9 rounded-full"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isSaving ? 'Saving…' : 'Save changes'}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>
  );
}

function DirtyIndicator({ diff }: { diff: MatrixDiff }) {
  const roleCount = diff.perRole.length;
  return (
    <span className="text-xs text-muted-foreground tabular-nums">
      <DiffCounters added={diff.added} removed={diff.removed} />
      <span>
        {' '}
        across {roleCount} {roleCount === 1 ? 'role' : 'roles'}
      </span>
    </span>
  );
}

function DiffCounters({ added, removed }: { added: number; removed: number }) {
  return (
    <>
      {added > 0 && <span className="text-emerald-600 dark:text-emerald-400">+{added}</span>}
      {added > 0 && removed > 0 && <span className="text-muted-foreground"> / </span>}
      {removed > 0 && <span className="text-red-600 dark:text-red-400">−{removed}</span>}
    </>
  );
}

interface ConfirmSaveDialogProps {
  open: boolean;
  diff: MatrixDiff;
  isSaving: boolean;
  error: string | null;
  onCancel: () => void;
  onApply: () => void;
}

function ConfirmSaveDialog({
  open,
  diff,
  isSaving,
  error,
  onCancel,
  onApply,
}: ConfirmSaveDialogProps) {
  const total = diff.added + diff.removed;
  const roleCount = diff.perRole.length;
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (isSaving) return;
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Apply permission changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You&rsquo;re about to{' '}
            {diff.added > 0 && (
              <>
                add <span className="font-medium text-foreground">{diff.added}</span>
                {diff.removed > 0 && ' and '}
              </>
            )}
            {diff.removed > 0 && (
              <>
                remove <span className="font-medium text-foreground">{diff.removed}</span>
              </>
            )}{' '}
            permission{total === 1 ? '' : 's'} across{' '}
            <span className="font-medium text-foreground">{roleCount}</span>{' '}
            {roleCount === 1 ? 'role' : 'roles'}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="text-sm space-y-1 max-h-40 overflow-y-auto rounded-md border border-border bg-muted/30 p-3">
          {diff.perRole.map(({ role, added, removed }) => (
            <li key={role.id} className="flex items-center justify-between gap-3">
              <span className="text-foreground">{USER_ROLE_LABELS[role.name] || role.name}</span>
              <span className="tabular-nums text-xs">
                <DiffCounters added={added} removed={removed} />
              </span>
            </li>
          ))}
        </ul>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onApply();
            }}
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Module Group Row ──────────────────────────────────────

interface ModuleGroupProps {
  moduleName: string;
  permissions: Permission[];
  roles: RoleWithPermissions[];
  isExpanded: boolean;
  isSuperAdmin: boolean;
  canEditRole: (role: RoleWithPermissions) => boolean;
  onToggleExpand: () => void;
  onTogglePermission: (role: RoleWithPermissions, perm: Permission) => void;
  onToggleModule: (role: RoleWithPermissions) => void;
  roleHasPermission: (role: RoleWithPermissions, permId: string) => boolean;
}

function ModuleGroup({
  moduleName,
  permissions,
  roles,
  isExpanded,
  isSuperAdmin,
  canEditRole,
  onToggleExpand,
  onTogglePermission,
  onToggleModule,
  roleHasPermission,
}: ModuleGroupProps) {
  return (
    <>
      <tr
        className="group border-b border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
        onClick={onToggleExpand}
      >
        <td className="p-3 sticky left-0 z-10 bg-background transition-colors before:content-[''] before:absolute before:inset-0 before:bg-muted/20 group-hover:before:bg-muted/40 before:transition-colors before:pointer-events-none">
          <div className="relative flex items-center gap-2 font-medium text-foreground">
            <div className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </div>
            <span className="capitalize text-sm">{moduleName}</span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
              {permissions.length}
            </Badge>
          </div>
        </td>
        {roles.map((role) => {
          // Counts only what this user may change: see permission-matrix.ts. [HW-1061]
          const moduleState = moduleCheckState(
            permissions,
            (permId) => roleHasPermission(role, permId),
            isSuperAdmin
          );
          const { checked } = moduleState;
          const editable = moduleState.editable && canEditRole(role);
          return (
            <td key={role.id} className="text-center p-3">
              <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggleModule(role)}
                  disabled={!editable}
                  className={editable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                />
              </div>
            </td>
          );
        })}
      </tr>
      {isExpanded &&
        permissions.map((perm) => (
          <tr
            key={perm.id}
            className="border-b border-border/50 hover:bg-muted/20 transition-colors"
          >
            <td className="p-3 pl-10 sticky left-0 bg-background z-10">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <div className="flex items-center gap-1.5 text-foreground">
                        {perm.name}
                        {isSuperAdminOnlyCode(perm.code) && !isSuperAdmin && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 text-muted-foreground"
                          >
                            Super Admin only
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{perm.code}</div>
                    </div>
                  </TooltipTrigger>
                  {perm.description && (
                    <TooltipContent side="right">
                      <p className="max-w-xs">{perm.description}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </td>
            {roles.map((role) => {
              const isRestricted =
                (isSuperAdminOnlyCode(perm.code) && !isSuperAdmin) || !canEditRole(role);
              return (
                <td key={role.id} className="text-center p-3">
                  <div className="flex justify-center">
                    <Checkbox
                      checked={roleHasPermission(role, perm.id)}
                      onCheckedChange={() => onTogglePermission(role, perm)}
                      disabled={isRestricted}
                      className={isRestricted ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                    />
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
    </>
  );
}

// ─── Roles Tab ─────────────────────────────────────────────

function RolesTab() {
  const { data: roles, isLoading } = useRoles();
  const isSuperAdmin = useIsSuperAdmin();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleWithPermissions | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleWithPermissions | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const resetForm = () => setFormData({ name: '', description: '' });

  const handleCreate = async () => {
    await createMutation.mutateAsync({
      name: formData.name,
      description: formData.description || undefined,
    });
    setCreateOpen(false);
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editRole) return;
    await updateMutation.mutateAsync({
      id: editRole.id,
      name: formData.name,
      description: formData.description || undefined,
    });
    setEditRole(null);
    resetForm();
  };

  const handleDelete = async () => {
    if (!deleteRole) return;
    await deleteMutation.mutateAsync(deleteRole.id);
    setDeleteRole(null);
  };

  const openEdit = (role: RoleWithPermissions) => {
    setFormData({ name: role.name, description: role.description || '' });
    setEditRole(role);
  };

  // Master–detail: the role shown in the right pane. Defaults to the first role.
  const selectedRole = roles?.find((r) => r.id === selectedId) ?? roles?.[0] ?? null;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Creating a role stays with super_admin, like editing and deleting one. [HW-1062] */}
      {isSuperAdmin && (
        <div className="flex justify-end">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="h-9 w-9 cursor-pointer rounded-full"
                  onClick={() => {
                    resetForm();
                    setCreateOpen(true);
                  }}
                  aria-label="Create role"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Create role</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {!roles?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-8 w-8 text-muted-foreground mb-4" />
            <h3 className="font-medium text-foreground mb-1">No roles yet</h3>
            <p className="text-sm text-muted-foreground">Create your first role to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-border bg-card md:grid-cols-[280px_1fr]">
          {/* Left rail — role list */}
          <div className="border-b border-border p-2 md:max-h-[640px] md:overflow-y-auto md:border-b-0 md:border-r">
            {roles.map((role) => {
              const active = selectedRole?.id === role.id;
              const userCount = role._count?.users ?? 0;
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedId(role.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                    active ? 'bg-hygge/10' : 'hover:bg-muted/50'
                  )}
                >
                  <div
                    className={cn(
                      'rounded-lg p-1.5',
                      ROLE_COLORS[role.name] || 'bg-muted text-muted-foreground'
                    )}
                  >
                    <Shield className="h-3.5 w-3.5" />
                  </div>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-sm font-medium',
                        active ? 'text-hygge' : 'text-foreground'
                      )}
                    >
                      {USER_ROLE_LABELS[role.name] || role.name}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {userCount} {userCount === 1 ? 'user' : 'users'}
                    </span>
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {role.permissions.length}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right pane — selected role detail */}
          {selectedRole && (
            <RoleDetail
              role={selectedRole}
              onEdit={() => openEdit(selectedRole)}
              onDelete={() => setDeleteRole(selectedRole)}
            />
          )}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
            <DialogDescription>
              Create a new role. You can assign permissions afterwards.
            </DialogDescription>
          </DialogHeader>
          <RoleForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formData.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editRole} onOpenChange={(open) => !open && setEditRole(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>Update the role name and description.</DialogDescription>
          </DialogHeader>
          <RoleForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRole(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formData.name.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteRole} onOpenChange={(open) => !open && setDeleteRole(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the role &ldquo;{deleteRole?.name}&rdquo;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteRole?.isSystem && (
            <p className="text-sm text-destructive">System roles cannot be deleted.</p>
          )}
          {(deleteRole?._count?.users ?? 0) > 0 && (
            <p className="text-sm text-destructive">
              This role has {deleteRole?._count?.users} assigned account(s). Reassign them first.
            </p>
          )}
          {deleteMutation.isError && (
            <p className="text-sm text-destructive">
              {(deleteMutation.error as Error)?.message || 'Failed to delete role'}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={
                deleteRole?.isSystem ||
                (deleteRole?._count?.users ?? 0) > 0 ||
                deleteMutation.isPending
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Role Detail (master–detail right pane) ────────────────

interface RoleDetailProps {
  role: RoleWithPermissions;
  onEdit: () => void;
  onDelete: () => void;
}

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'r-super-admin',
  hr_admin: 'r-hr-admin',
  hr_manager: 'r-hr-manager',
  manager: 'r-manager',
  employee: 'r-employee',
};

function RoleDetail({ role, onEdit, onDelete }: RoleDetailProps) {
  const isSuperAdmin = useIsSuperAdmin();
  const userCount = role._count?.users ?? 0;

  const moduleGroups = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of role.permissions) {
      const list = map.get(p.module) || [];
      list.push(p);
      map.set(p.module, list);
    }
    return Array.from(map.entries());
  }, [role.permissions]);

  const colorClass = ROLE_COLORS[role.name] || 'bg-muted text-muted-foreground';

  return (
    <div className="p-6 md:max-h-[640px] md:overflow-y-auto">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className={cn('rounded-xl p-2.5', colorClass)}>
          <Shield className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              {USER_ROLE_LABELS[role.name] || role.name}
            </h3>
            {role.isSystem && (
              <Badge variant="secondary" className="text-[10px]">
                System
              </Badge>
            )}
          </div>
          {role.description && (
            <p className="mt-0.5 text-sm text-muted-foreground">{role.description}</p>
          )}
          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              <b className="font-semibold text-foreground">{userCount}</b>
              {userCount === 1 ? 'user' : 'users'}
            </span>
            <span>
              <b className="font-semibold text-foreground">{role.permissions.length}</b> permissions
            </span>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <TooltipProvider delayDuration={150}>
            {!role.isSystem && isSuperAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                    onClick={onEdit}
                    aria-label="Edit role"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit role</TooltipContent>
              </Tooltip>
            )}
            {!role.isSystem && isSuperAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 cursor-pointer rounded-full text-muted-foreground hover:text-destructive"
                    onClick={onDelete}
                    aria-label="Delete role"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete role</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* Members */}
      {role.users && role.users.length > 0 && (
        <div className="mt-6">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Members ({role.users.length})
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {role.users.map((user) => (
              <RoleUserItem key={user.id} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* Permissions — grouped by module as chips */}
      <div className="mt-6">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Permissions ({role.permissions.length})
        </p>
        {moduleGroups.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
            No permissions assigned
          </div>
        ) : (
          <div className="space-y-4">
            {moduleGroups.map(([moduleName, perms]) => (
              <div key={moduleName}>
                <p className="mb-1.5 text-[11px] font-medium capitalize tracking-wide text-muted-foreground/70">
                  {moduleName}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {perms.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Role User Item ────────────────────────────────────────

function RoleUserItem({ user }: { user: RoleUser }) {
  const formatName = useEmployeeNameFormatter();
  const name = user.employee ? formatName({ firstName: user.employee.firstName, lastName: user.employee.lastName }) : user.email;
  const initials = user.employee
    ? `${user.employee.firstName.charAt(0)}${user.employee.lastName.charAt(0)}`.toUpperCase()
    : user.email.charAt(0).toUpperCase();
  const linkTo = user.employee ? `/employees/${user.employee.id}` : undefined;

  const content = (
    <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-muted/50 transition-colors">
      <Avatar className="h-7 w-7">
        {user.employee?.avatarUrl && <AvatarImage src={user.employee.avatarUrl} alt={name} />}
        <AvatarFallback className="text-[10px] font-medium">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground truncate">{name}</p>
        {user.employee && (
          <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}

// ─── Role Form ─────────────────────────────────────────────

interface RoleFormData {
  name: string;
  description: string;
}

interface RoleFormProps {
  formData: RoleFormData;
  setFormData: (data: RoleFormData) => void;
}

function RoleForm({ formData, setFormData }: RoleFormProps) {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="role-name">Name</Label>
        <Input
          id="role-name"
          placeholder="e.g., content_editor"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="role-desc">Description</Label>
        <Textarea
          id="role-desc"
          placeholder="Describe what this role is for..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />
      </div>
    </div>
  );
}

// ─── Accounts Tab ──────────────────────────────────────────

function UserRolesTab() {
  const formatName = useEmployeeNameFormatter();
  const { data: roles, isLoading } = useRoles();
  const assignMutation = useAssignRoleToUsers();
  const deleteUserMutation = useDeleteUser();
  const isSuperAdmin = useIsSuperAdmin();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [createTarget, setCreateTarget] = useState<{ user: RoleUser; roleId: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleUser | null>(null);
  const queryClient = useQueryClient();

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUserMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // error text is rendered inside the dialog via deleteUserMutation.error
    }
  };

  // Flatten all users from all roles
  const allUsers = useMemo(() => {
    if (!roles) return [];
    const userMap = new Map<string, { user: RoleUser; roleId: string; roleName: string }>();
    for (const role of roles) {
      if (!role.users) continue;
      for (const user of role.users) {
        userMap.set(user.id, {
          user,
          roleId: role.id,
          roleName: USER_ROLE_LABELS[role.name] || role.name,
        });
      }
    }
    return Array.from(userMap.values());
  }, [roles]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return allUsers;
    // Match each query token against firstName/lastName/email separately, so search is
    // independent of the display order (typing "First Last" or "Last First" both hit).
    const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
    return allUsers.filter(({ user }) => {
      const fields = [
        user.employee?.firstName ?? '',
        user.employee?.lastName ?? '',
        user.email,
      ].map((f) => f.toLowerCase());
      return tokens.every((t) => fields.some((f) => f.includes(t)));
    });
  }, [allUsers, search]);

  const toggleUser = (userId: string) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(({ user }) => user.id)));
    }
  };

  const handleBulkAssign = async (roleId: string) => {
    if (!roleId || selectedUsers.size === 0) return;
    await assignMutation.mutateAsync({
      roleId,
      userIds: Array.from(selectedUsers),
    });
    setSelectedUsers(new Set());
  };

  const handleSingleChange = async (userId: string, roleId: string) => {
    await assignMutation.mutateAsync({
      roleId,
      userIds: [userId],
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-3 border-b border-border/60 bg-muted/20 px-5 py-3">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 border-transparent bg-background/60 pl-9 shadow-sm focus-visible:border-hygge/40 focus-visible:ring-hygge/20"
              />
            </div>

            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'account' : 'accounts'}
            </span>

            {/* Moving accounts between roles stays with super_admin (PUT /roles/assign-users). [HW-1062] */}
            {isSuperAdmin && selectedUsers.size > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-hygge/30 bg-hygge/5 px-2.5 py-1.5">
                <span className="text-xs font-medium text-hygge">
                  {selectedUsers.size} selected
                </span>
                <div className="h-4 w-px bg-hygge/20" />
                <Select onValueChange={handleBulkAssign}>
                  <SelectTrigger className="h-7 w-[160px] border-0 bg-transparent text-xs shadow-none focus:ring-0">
                    <SelectValue placeholder="Assign role…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {USER_ROLE_LABELS[role.name] || role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="py-2.5 pl-5 pr-2 w-10">
                    {/* Selecting feeds only the bulk assign, which is super_admin's. [HW-1062] */}
                    {isSuperAdmin && (
                      <Checkbox
                        checked={
                          filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length
                            ? true
                            : selectedUsers.size > 0
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={toggleAll}
                        className="cursor-pointer"
                      />
                    )}
                  </th>
                  <th className="py-2.5 px-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Account
                  </th>
                  <th className="py-2.5 px-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground w-[220px]">
                    Role
                  </th>
                  <th className="py-2.5 pr-5 pl-2 w-[120px]" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(({ user, roleId }) => {
                  const hasEmployee = !!user.employee;
                  const emailLocal = (user.email.split('@')[0] ?? '').trim();
                  const displayName = hasEmployee
                    ? formatName({ firstName: user.employee!.firstName, lastName: user.employee!.lastName })
                    : emailLocal || user.email;
                  const initials = hasEmployee
                    ? `${user.employee!.firstName.charAt(0)}${user.employee!.lastName.charAt(0)}`.toUpperCase()
                    : (emailLocal || user.email).charAt(0).toUpperCase();
                  const roleName = roles?.find((r) => r.id === roleId)?.name ?? '';
                  const roleColorClass = ROLE_COLORS[roleName] || 'bg-muted text-muted-foreground';
                  const isSelected = selectedUsers.has(user.id);
                  const isSelf = user.id === currentUserId;

                  return (
                    <tr
                      key={user.id}
                      className={cn(
                        'group border-b border-border/40 transition-colors',
                        isSelected ? 'bg-hygge/5' : 'hover:bg-muted/30'
                      )}
                    >
                      <td className="py-3.5 pl-5 pr-2 align-middle">
                        {isSuperAdmin && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleUser(user.id)}
                            className="cursor-pointer"
                            aria-label={`Select ${user.email}`}
                          />
                        )}
                      </td>
                      <td className="py-3.5 px-2 align-middle">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            className={cn(
                              'h-10 w-10 rounded-xl ring-1 ring-border/60 shrink-0',
                              hasEmployee && 'ring-hygge/30'
                            )}
                          >
                            {user.employee?.avatarUrl && (
                              <AvatarImage src={user.employee.avatarUrl} alt={displayName} />
                            )}
                            <AvatarFallback
                              className={cn(
                                'rounded-xl text-xs font-semibold',
                                hasEmployee
                                  ? 'bg-hygge/10 text-hygge'
                                  : 'bg-muted text-muted-foreground'
                              )}
                            >
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col min-w-0">
                            {hasEmployee ? (
                              <>
                                <RouterLink
                                  to="/employees/$id"
                                  params={{ id: user.employee!.id }}
                                  className="font-medium text-foreground hover:text-hygge transition-colors truncate leading-tight"
                                  title={`Open ${displayName}'s profile`}
                                >
                                  {displayName}
                                </RouterLink>
                                <span className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
                                  {user.email}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="font-medium text-foreground truncate leading-tight">
                                  {user.email}
                                </span>
                                <span className="inline-flex items-center gap-1 mt-1 w-fit text-[10px] uppercase tracking-wider font-medium text-amber-600 dark:text-amber-500">
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                                  No employee profile
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-2 align-middle">
                        {/* Read-only for anyone but super_admin, like the bulk assign. [HW-1062] */}
                        <Select
                          value={roleId}
                          onValueChange={(value) => handleSingleChange(user.id, value)}
                          disabled={!isSuperAdmin}
                        >
                          <SelectTrigger
                            className={cn(
                              'h-8 w-auto min-w-[140px] gap-2 border-0 rounded-full px-3 text-xs font-medium shadow-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background',
                              roleColorClass
                            )}
                          >
                            <Shield className="h-3 w-3 opacity-70" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {roles?.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                {USER_ROLE_LABELS[role.name] || role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3.5 pr-5 pl-2 align-middle text-right whitespace-nowrap">
                        {isSuperAdmin ? (
                          <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted/40 p-0.5 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            {!hasEmployee && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => setCreateTarget({ user, roleId })}
                                    aria-label={`Create an employee profile for ${user.email}`}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-hygge/10 hover:text-hygge focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hygge/40 cursor-pointer"
                                  >
                                    <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" sideOffset={6}>
                                  Create employee profile
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={isSelf ? 0 : -1} className="inline-flex">
                                  <button
                                    type="button"
                                    onClick={() => setDeleteTarget(user)}
                                    disabled={isSelf}
                                    aria-label={
                                      isSelf
                                        ? 'You cannot delete your own account'
                                        : `Delete user account for ${user.email}`
                                    }
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground cursor-pointer"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                  </button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6}>
                                {isSelf
                                  ? 'You cannot delete your own account'
                                  : 'Delete user account'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        ) : hasEmployee ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400"
                            aria-label="Employee profile linked"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Linked
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60">
                        <Search className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="mt-4 text-sm font-medium text-foreground">
                        No users match your search
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Try a different email or name
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>

        {createTarget && (
          <AddEmployeeDialog
            open={!!createTarget}
            onOpenChange={(open) => {
              if (!open) setCreateTarget(null);
            }}
            title="Create Employee from User"
            description={`Link a new employee profile to ${createTarget.user.email}.`}
            defaults={{
              ...guessNameFromEmail(createTarget.user.email),
              workEmail: createTarget.user.email,
              workEmailLocked: true,
              roleId: createTarget.roleId,
              startDate: new Date(),
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
              setCreateTarget(null);
            }}
          />
        )}

        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteTarget(null);
              deleteUserMutation.reset();
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete user account</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.employee ? (
                  <>
                    This removes the sign-in account for{' '}
                    <span className="font-medium text-foreground">
                      {formatName({ firstName: deleteTarget.employee.firstName, lastName: deleteTarget.employee.lastName })}
                    </span>{' '}
                    ({deleteTarget.email}). The employee profile stays intact and is simply unlinked
                    — you can later create a new user for them or link an existing one.
                  </>
                ) : (
                  <>
                    This removes the sign-in account for{' '}
                    <span className="font-medium text-foreground">{deleteTarget?.email}</span>. No
                    employee profile is linked to this account, so nothing else is affected.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {deleteUserMutation.isError && (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {extractErrorMessage(
                  deleteUserMutation.error,
                  'Failed to delete the user. Please try again.'
                )}
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                disabled={deleteUserMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteUserMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete user
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </TooltipProvider>
  );
}

// ─── Permissions Tab ───────────────────────────────────────

function PermissionsTab() {
  const { data: permissions, isLoading } = useAllPermissions();
  const createMutation = useCreatePermission();
  const deleteMutation = useDeletePermission();
  // The catalog is changed under its registry code, not a role name. [HW-1062]
  const canManagePermissions = usePermission('settings.permissions.manage');
  // A row granting a super-admin-only code is deleted by super_admin only. [HW-1062]
  const isSuperAdmin = useIsSuperAdmin();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PermissionWithRoleCount | null>(null);
  const [search, setSearch] = useState('');
  const [permForm, setPermForm] = useState({ code: '', name: '', description: '', module: '' });

  const modules = useMemo(() => {
    if (!permissions) return [];
    const map = new Map<string, PermissionWithRoleCount[]>();
    for (const p of permissions) {
      const list = map.get(p.module) || [];
      list.push(p);
      map.set(p.module, list);
    }
    return Array.from(map.entries());
  }, [permissions]);

  const filteredModules = useMemo(() => {
    if (!search.trim()) return modules;
    const q = search.toLowerCase();
    return modules
      .map(
        ([name, perms]) =>
          [
            name,
            perms.filter(
              (p) => p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q)
            ),
          ] as [string, PermissionWithRoleCount[]]
      )
      .filter(([, perms]) => perms.length > 0);
  }, [modules, search]);

  const handleCreate = async () => {
    await createMutation.mutateAsync({
      code: permForm.code,
      name: permForm.name,
      description: permForm.description || undefined,
      module: permForm.module,
    });
    setCreateOpen(false);
    setPermForm({ code: '', name: '', description: '', module: '' });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search permissions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        {canManagePermissions && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                  onClick={() => {
                    setPermForm({ code: '', name: '', description: '', module: '' });
                    setCreateOpen(true);
                  }}
                  aria-label="Create permission"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Create permission</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {filteredModules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-8 w-8 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">No permissions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredModules.map(([moduleName, perms]) => (
            <Card key={moduleName}>
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-foreground capitalize mb-3 flex items-center gap-2">
                  {moduleName}
                  <Badge variant="secondary" className="text-[10px]">
                    {perms.length}
                  </Badge>
                </h3>
                <div className="space-y-2">
                  {perms.map((perm) => (
                    <div
                      key={perm.id}
                      className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors group"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">{perm.name}</span>
                          {perm.isSystem && (
                            <Badge variant="outline" className="text-[10px]">
                              System
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <code className="text-xs text-muted-foreground font-mono">
                            {perm.code}
                          </code>
                          <span className="text-xs text-muted-foreground">
                            · {perm._count?.rolePermissions ?? 0} roles
                          </span>
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <TooltipProvider>
                          <Tooltip>
                            {/* The span takes the hover: a disabled button fires no
                                pointer events, so its reason would never show. [HW-1062] */}
                            <TooltipTrigger asChild>
                              <span
                                // Focusable only while the button is not, so the
                                // reason is reachable by keyboard too.
                                tabIndex={
                                  perm.isSystem ||
                                  !canManagePermissions ||
                                  (isSuperAdminOnlyCode(perm.code) && !isSuperAdmin)
                                    ? 0
                                    : -1
                                }
                                className="inline-flex"
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => setDeleteTarget(perm)}
                                  disabled={
                                    perm.isSystem ||
                                    !canManagePermissions ||
                                    (isSuperAdminOnlyCode(perm.code) && !isSuperAdmin)
                                  }
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {perm.isSystem
                                ? 'System permissions cannot be deleted'
                                : !canManagePermissions
                                  ? 'Deleting permissions needs Create and delete permissions'
                                  : isSuperAdminOnlyCode(perm.code) && !isSuperAdmin
                                    ? 'Only Super Admin can delete this permission'
                                    : 'Delete'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Permission Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Permission</DialogTitle>
            <DialogDescription>
              Define a new permission that can be assigned to roles.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="perm-code">Code</Label>
              <Input
                id="perm-code"
                placeholder="e.g., reports.analytics.view"
                value={permForm.code}
                onChange={(e) => setPermForm({ ...permForm, code: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Use dot notation: module.resource.action
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="perm-name">Name</Label>
              <Input
                id="perm-name"
                placeholder="e.g., View analytics reports"
                value={permForm.name}
                onChange={(e) => setPermForm({ ...permForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="perm-module">Module</Label>
              <Input
                id="perm-module"
                placeholder="e.g., reports"
                value={permForm.module}
                onChange={(e) => setPermForm({ ...permForm, module: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="perm-desc">Description (optional)</Label>
              <Textarea
                id="perm-desc"
                placeholder="What this permission controls..."
                value={permForm.description}
                onChange={(e) => setPermForm({ ...permForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          {createMutation.isError && (
            <p className="text-sm text-destructive">
              {(createMutation.error as Error)?.message || 'Failed to create permission'}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={
                !permForm.code.trim() ||
                !permForm.name.trim() ||
                !permForm.module.trim() ||
                createMutation.isPending
              }
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Permission Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Permission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo; (
              <code className="text-xs">{deleteTarget?.code}</code>)? This will remove it from all
              roles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget?.isSystem && (
            <p className="text-sm text-destructive">System permissions cannot be deleted.</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteTarget?.isSystem || deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
